import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, MessageCircle, Menu, X, Check } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { whatsappLink } from "../i18n/translations";
import { Logo } from "./Logo";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export const Navbar = () => {
  const { t, lang, setLang, languages } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "villas", label: t.nav_villas },
    { id: "services", label: t.nav_services },
    { id: "vision", label: t.nav_vision },
    { id: "about", label: t.nav_about },
    { id: "contact", label: t.nav_contact },
  ];

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-black/80 backdrop-blur-xl border-b border-[rgba(197,160,89,0.15)]" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between gap-4">
        <Logo />

        <nav className="hidden lg:flex items-center gap-8" data-testid="navbar-links">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => scrollTo(l.id)}
              className="text-xs tracking-[0.22em] uppercase text-[#C7BFA7] hover:text-[#EED8A1] transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="relative">
            <button
              data-testid="language-selector-button"
              onClick={() => setLangOpen((o) => !o)}
              className="flex items-center gap-2 px-3 py-2 border border-[rgba(197,160,89,0.35)] text-[#EED8A1] text-xs tracking-[0.18em] uppercase hover:bg-[rgba(197,160,89,0.08)] transition-colors duration-300"
            >
              <Globe size={15} />
              <span>{t.nav_language}</span>
            </button>
            <AnimatePresence>
              {langOpen && (
                <motion.div
                  data-testid="language-dropdown-menu"
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2 }}
                  className="absolute end-0 top-full mt-2 w-44 bg-[#0A0A0A] border border-[rgba(197,160,89,0.3)] shadow-[0_20px_60px_rgba(0,0,0,0.8)]"
                >
                  {languages.map((l) => (
                    <button
                      key={l.code}
                      data-testid={`language-option-${l.code}`}
                      onClick={() => {
                        setLang(l.code);
                        setLangOpen(false);
                      }}
                      className="w-full flex items-center justify-between px-4 py-3 text-sm text-[#C7BFA7] hover:bg-[rgba(197,160,89,0.1)] hover:text-[#F4E5C2] transition-colors"
                    >
                      <span>{l.label}</span>
                      {lang === l.code && <Check size={14} className="text-[#C5A059]" />}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <a
            data-testid="whatsapp-header-cta"
            href={whatsappLink(t.whatsapp_message)}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:flex items-center gap-2 px-4 py-2 bg-[#C5A059] text-black text-xs font-semibold tracking-[0.14em] uppercase hover:bg-[#EED8A1] transition-colors duration-300"
          >
            <MessageCircle size={15} />
            <span>WhatsApp</span>
          </a>

          <button
            data-testid="mobile-menu-button"
            onClick={() => setMenuOpen((o) => !o)}
            className="lg:hidden text-[#EED8A1] p-2"
            aria-label="Menu"
          >
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-black/95 backdrop-blur-xl border-b border-[rgba(197,160,89,0.15)] overflow-hidden"
          >
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-nav-link-${l.id}`}
                onClick={() => {
                  scrollTo(l.id);
                  setMenuOpen(false);
                }}
                className="block w-full text-start px-6 py-4 text-sm tracking-[0.2em] uppercase text-[#C7BFA7] border-b border-[rgba(197,160,89,0.08)]"
              >
                {l.label}
              </button>
            ))}
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
